/**
 * ReverseActionButton — the one client-side pattern for a governed reversal
 * (migration 738; typed wrappers in `src/lib/reversals/reversalsService.ts`).
 *
 * Every reversal RPC requires a written reason and can be refused once
 * something downstream has consumed the act — the refusal names what blocks
 * it (`blockedBy`) and, often, a remedy. This button is the single place that
 * enforces the reason, calls the RPC, and renders that refusal, so every
 * mount point (Track unbind, agent-inbox reopen, cycle unarchive, invitation
 * revoke, org-unit restore, plan un-placement, AI-item un-review) shows the
 * same behavior instead of seven hand-rolled confirms.
 *
 * Visibility is the caller's job: pass this component only to users who
 * could plausibly be allowed (the doer, or an admin/owner where the page
 * already knows the role). This button never suppresses what the server
 * says — a refusal always reaches the modal, regardless of who clicked.
 */
import { useState } from 'react';
import { Button, type ButtonProps } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { ReasonModal } from './ReasonModal';
import type { ReversalResult } from '@/lib/reversals/reversalsService';

export interface ReverseActionButtonProps {
  /** Button text, and the confirm button's label inside the modal. */
  label: string;
  /** Modal title, e.g. "Unbind from cycle". */
  confirmTitle: string;
  /** Plain statement of what will be undone. */
  explain: React.ReactNode;
  /** Calls the reversal RPC with the typed reason and returns its result. */
  run: (reason: string) => Promise<ReversalResult>;
  /** Called after a successful reversal — typically a refetch. */
  onDone?: () => void;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
  disabled?: boolean;
  title?: string;
  /** Open the reason dialog from elsewhere (a menu item) instead of this button. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Draw only the dialog; the caller opens it through `open`. */
  hideButton?: boolean;
}

export function ReverseActionButton({
  label,
  confirmTitle,
  explain,
  run,
  onDone,
  variant = 'secondary',
  size = 'sm',
  className,
  disabled,
  title,
  open: controlledOpen,
  onOpenChange,
  hideButton,
}: ReverseActionButtonProps) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlledOpen ?? ownOpen;
  const setOpen = (next: boolean) => {
    setOwnOpen(next);
    onOpenChange?.(next);
  };
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { showToast } = useToast();

  const handleConfirm = async (reason: string) => {
    if (!reason.trim()) return;
    setBusy(true);
    setErrorMessage(null);
    try {
      const result = await run(reason);
      if (result.ok) {
        showToast('success', label);
        setOpen(false);
        onDone?.();
      } else if (result.blockedBy) {
        setErrorMessage(
          `Can't undo this: ${result.blockedBy}${result.remedy ? ` — ${result.remedy}` : ''}`
        );
      } else {
        setErrorMessage(result.error || 'This could not be undone.');
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'This could not be undone.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!hideButton && (
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        disabled={disabled}
        title={title}
        onClick={() => {
          setErrorMessage(null);
          setOpen(true);
        }}
      >
        {label}
      </Button>
      )}
      <ReasonModal
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        title={confirmTitle}
        explain={explain}
        confirmLabel={label}
        busy={busy}
        errorMessage={errorMessage}
        onConfirm={handleConfirm}
      />
    </>
  );
}
